import React, { useState, useEffect } from 'react';
import { 
  Gift, Send, Clock, Trash2, Plus, Users, UserPlus, 
  ShoppingCart, Calendar, CheckCircle, AlertCircle, X,
  Megaphone, Percent, Tag
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { promotionsAPI } from '../services/api';
import { toast } from 'sonner';

const audienceConfig = {
  all: { label: 'All Users', icon: Users, color: 'blue' },
  new_users: { label: 'New Users (≤1 order)', icon: UserPlus, color: 'green' },
  frequent_shoppers: { label: 'Frequent Shoppers (5+ orders)', icon: ShoppingCart, color: 'purple' },
};

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  sending: { label: 'Sending', color: 'bg-yellow-100 text-yellow-700' },
  sent: { label: 'Sent', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const PromotionalNotifications = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sending, setSending] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    discount_code: '',
    discount_percent: '',
    target_audience: 'all',
    scheduled_at: '',
    send_now: false,
  });

  // Fetch promotions
  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const data = await promotionsAPI.getAll();
      setPromotions(data);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  // Handle form change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Create promotion
  const handleCreatePromotion = async (sendNow = false) => {
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Please fill in title and message');
      return;
    }

    try {
      const promoData = {
        title: formData.title,
        message: formData.message,
        discount_code: formData.discount_code || null,
        discount_percent: formData.discount_percent ? parseFloat(formData.discount_percent) : null,
        target_audience: formData.target_audience,
        scheduled_at: formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : null,
        send_now: sendNow,
      };

      await promotionsAPI.create(promoData);
      toast.success(sendNow ? 'Promotion sent successfully!' : 'Promotion created!');
      setShowCreateModal(false);
      setFormData({
        title: '',
        message: '',
        discount_code: '',
        discount_percent: '',
        target_audience: 'all',
        scheduled_at: '',
        send_now: false,
      });
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to create promotion');
    }
  };

  // Send promotion
  const handleSendPromotion = async (promoId) => {
    try {
      setSending(promoId);
      const result = await promotionsAPI.send(promoId);
      toast.success(`Promotion sent to ${result.sent_count} users!`);
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to send promotion');
    } finally {
      setSending(null);
    }
  };

  // Delete promotion
  const handleDeletePromotion = async (promoId) => {
    if (!confirm('Are you sure you want to delete this promotion?')) return;
    
    try {
      await promotionsAPI.delete(promoId);
      toast.success('Promotion deleted');
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to delete promotion');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="promotional-notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1 flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-orange-500" />
            Promotional Notifications
          </h1>
          <p className="text-gray-500">Create and send promotional messages to customers</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="create-promo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Promotion
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">
                {promotions.filter(p => p.status === 'sent').length}
              </p>
              <p className="text-sm text-gray-500">Sent</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">
                {promotions.filter(p => p.status === 'scheduled').length}
              </p>
              <p className="text-sm text-gray-500">Scheduled</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Gift className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">
                {promotions.filter(p => p.status === 'draft').length}
              </p>
              <p className="text-sm text-gray-500">Drafts</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Promotions List */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h2 className="font-semibold text-gray-800">All Promotions</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : promotions.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="no-promotions">
            <Gift className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="font-medium">No promotions yet</p>
            <p className="text-sm">Create your first promotional notification!</p>
          </div>
        ) : (
          <div className="divide-y" data-testid="promotions-list">
            {promotions.map((promo) => {
              const status = statusConfig[promo.status] || statusConfig.draft;
              const audience = audienceConfig[promo.target_audience] || audienceConfig.all;
              const AudienceIcon = audience.icon;
              
              return (
                <div key={promo.id} className="p-4 hover:bg-gray-50" data-testid={`promo-${promo.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-800 truncate">{promo.title}</h3>
                        <Badge className={status.color}>{status.label}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{promo.message}</p>
                      
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <AudienceIcon className="w-3 h-3" />
                          {audience.label}
                        </span>
                        {promo.discount_code && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            Code: {promo.discount_code}
                          </span>
                        )}
                        {promo.discount_percent && (
                          <span className="flex items-center gap-1">
                            <Percent className="w-3 h-3" />
                            {promo.discount_percent}% off
                          </span>
                        )}
                        {promo.sent_count > 0 && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-3 h-3" />
                            Sent to {promo.sent_count} users
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Created: {formatDate(promo.created_at)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {promo.status !== 'sent' && (
                        <Button
                          size="sm"
                          onClick={() => handleSendPromotion(promo.id)}
                          disabled={sending === promo.id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`send-promo-${promo.id}`}
                        >
                          {sending === promo.id ? (
                            'Sending...'
                          ) : (
                            <>
                              <Send className="w-3 h-3 mr-1" />
                              Send Now
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeletePromotion(promo.id)}
                        data-testid={`delete-promo-${promo.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-promo-modal">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Create Promotion</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowCreateModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Weekend Special 🎉"
                  value={formData.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  data-testid="promo-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  placeholder="Write your promotional message..."
                  value={formData.message}
                  onChange={(e) => handleFormChange('message', e.target.value)}
                  rows={3}
                  data-testid="promo-message-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="discount_code">Discount Code (optional)</Label>
                  <Input
                    id="discount_code"
                    placeholder="e.g., SAVE20"
                    value={formData.discount_code}
                    onChange={(e) => handleFormChange('discount_code', e.target.value.toUpperCase())}
                    data-testid="promo-code-input"
                  />
                </div>
                <div>
                  <Label htmlFor="discount_percent">Discount % (optional)</Label>
                  <Input
                    id="discount_percent"
                    type="number"
                    placeholder="e.g., 20"
                    min="0"
                    max="100"
                    value={formData.discount_percent}
                    onChange={(e) => handleFormChange('discount_percent', e.target.value)}
                    data-testid="promo-percent-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="target_audience">Target Audience</Label>
                <Select
                  value={formData.target_audience}
                  onValueChange={(value) => handleFormChange('target_audience', value)}
                >
                  <SelectTrigger data-testid="promo-audience-select">
                    <SelectValue placeholder="Select audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        All Users
                      </span>
                    </SelectItem>
                    <SelectItem value="new_users">
                      <span className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4" />
                        New Users (≤1 order)
                      </span>
                    </SelectItem>
                    <SelectItem value="frequent_shoppers">
                      <span className="flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4" />
                        Frequent Shoppers (5+ orders)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="scheduled_at">Schedule for Later (optional)</Label>
                <Input
                  id="scheduled_at"
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => handleFormChange('scheduled_at', e.target.value)}
                  data-testid="promo-schedule-input"
                />
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => handleCreatePromotion(false)}
                data-testid="save-draft-btn"
              >
                Save as Draft
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleCreatePromotion(true)}
                data-testid="send-immediately-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Immediately
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PromotionalNotifications;
